import { cn } from "@/lib/utils";
import { CHART_COLORS } from "../../_components/chartColors";
import type { Peer } from "../../_components/scoring-types";

const SCORE_SCALE = [1, 2, 3, 4, 5] as const;
const SCORE_MIN = 1;
const SCORE_MAX = 5;
const TRANSITION_EASING = "cubic-bezier(0.22, 1, 0.36, 1)";

/** Converts a score value to a percentage position along the score axis. */
const dotPosition = (score: number): string =>
  `${((score - SCORE_MIN) / (SCORE_MAX - SCORE_MIN)) * 100}%`;

interface PeerComparisonBarsProps {
  accountName: string;
  accountScore: number;
  peerColumns: (Peer | null)[];
  peerScores: (number | null)[];
}

export function PeerComparisonBars({
  accountName,
  accountScore,
  peerColumns,
  peerScores,
}: PeerComparisonBarsProps) {
  const peerEntries = peerColumns
    .map((peer, idx) => {
      if (!peer) return null;
      const score = peerScores[idx];
      if (score == null) return null;
      return { id: peer.id, name: peer.name, score, colorIdx: idx + 1 };
    })
    .filter(Boolean) as Array<{
    id: string;
    name: string;
    score: number;
    colorIdx: number;
  }>;

  const sortedPeers = [...peerEntries].sort((a, b) => b.score - a.score);
  const allEntries = [
    { id: "__account__", name: accountName, score: accountScore, colorIdx: 0 },
    ...sortedPeers,
  ];

  return (
    <div>
      {/* Scale header */}
      <div className="flex items-center mb-0.5">
        <div className="w-[300px] shrink-0" />
        <div className="flex-1 flex justify-between px-1">
          {SCORE_SCALE.map((n) => (
            <span
              key={n}
              className="text-xs text-muted-foreground w-4 text-center select-none"
            >
              {n}
            </span>
          ))}
        </div>
        <div className="w-[32px] shrink-0" />
      </div>
      {/* Rows */}
      <div className="overflow-y-auto max-h-[180px]">
        {allEntries.map((entry) => {
          const isAccount = entry.colorIdx === 0;
          const color = CHART_COLORS[entry.colorIdx];
          return (
            <div key={entry.id} className="flex items-center h-[30px]">
              <div
                className={cn(
                  "w-[300px] shrink-0 text-sm flex items-center gap-1.5 pr-2",
                  isAccount
                    ? "font-bold text-foreground"
                    : "font-medium text-muted-foreground",
                )}
              >
                <span
                  className={cn("rounded-full shrink-0", isAccount ? "size-[9px]" : "size-[7px]")}
                  style={{ background: color }}
                />
                <span className="truncate">{entry.name}</span>
              </div>
              <div className="flex-1 relative h-full px-1">
                {/* Base line */}
                <div
                  className="absolute top-1/2 left-1 right-1 h-px -translate-y-1/2"
                  style={{
                    background:
                      "color-mix(in oklch, var(--border), transparent 30%)",
                  }}
                />
                {/* Grid lines */}
                {SCORE_SCALE.map((n) => (
                  <div
                    key={n}
                    className="absolute top-0.5 bottom-0.5 w-px"
                    style={{
                      left: `calc(${dotPosition(n)} + 4px)`,
                      background:
                        "color-mix(in oklch, var(--border), transparent 60%)",
                    }}
                  />
                ))}
                {/* Fill line */}
                <div
                  className="absolute top-1/2 left-1 -translate-y-1/2 rounded-[1px] z-[1]"
                  style={{
                    width: dotPosition(entry.score),
                    height: "2px",
                    background: color,
                    transition: `width 0.6s ${TRANSITION_EASING}`,
                  }}
                />
                {/* Dot */}
                <div
                  className={cn("absolute top-1/2 rounded-full", isAccount ? "size-4 z-[3]" : "size-3 z-[2]")}
                  style={{
                    left: dotPosition(entry.score),
                    background: color,
                    border: `${isAccount ? "2.5px" : "2px"} solid var(--card)`,
                    transform: "translate(-50%, -50%)",
                    boxShadow: isAccount
                      ? `0 0 0 1.5px ${color}, 0 1px 4px rgba(0,0,0,0.15)`
                      : `0 0 0 1px ${color}, 0 1px 3px rgba(0,0,0,0.12)`,
                    transition: `left 0.6s ${TRANSITION_EASING}`,
                  }}
                />
              </div>
              <span
                className={cn(
                  "w-[32px] shrink-0 text-right text-sm pl-1",
                  isAccount
                    ? "font-semibold text-foreground"
                    : "font-medium text-muted-foreground",
                )}
              >
                {entry.score.toFixed(1)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
